/**
 * Generic ribbon renderer.
 *
 * It reads `RIBBON_TABS` and nothing else about the application: no tab id,
 * group id or action name appears below. Everything it needs is on `RibbonItem`
 * (see `ribbon/types.ts`). Keep it that way — a v2 tab must be a data change.
 *
 * Metrics follow the mockups: 34px tab strip, 92px band, 64px large buttons
 * with a 24px glyph, small buttons stacked three to a column, and a centred
 * grey group label under each group.
 */

import * as React from "react";

import { RIBBON_TABS } from "@/ribbon/ribbon.config";
import type { RibbonGroup, RibbonItem } from "@/ribbon/types";
import { dispatchAction, hasActionHandler } from "@/shell/actions";
import { resolveIcon } from "@/shell/icons";
import { useActiveTab, useAppStore, useRibbonContext, useRibbonMode, useToggles } from "@/store/appStore";
import type { RibbonMode } from "@/store/appStore";
import { cn } from "@/lib/utils";
import { ChevronsUp, ChevronDown, EyeOff, Pin } from "lucide-react";

export const RIBBON_BAND_HEIGHT = 92;

// ---------------------------------------------------------------- buttons ---

interface ItemProps {
  item: RibbonItem;
  disabled: boolean;
  pressed: boolean;
  onActivate: (item: RibbonItem) => void;
  onOpenMenu: (item: RibbonItem, anchor: HTMLElement) => void;
}

/** Shared press/hover skin so large and small buttons stay in step. */
function buttonSkin(disabled: boolean, pressed: boolean): string {
  return cn(
    "rounded border transition-colors duration-100",
    disabled
      ? "cursor-default border-transparent text-ads-faint opacity-50"
      : pressed
        ? "border-ads-accent-border bg-ads-accent-soft text-ads-accent"
        : "border-transparent text-ads-text hover:bg-ads-hover active:bg-ads-active",
  );
}

function LargeItem({ item, disabled, pressed, onActivate, onOpenMenu }: ItemProps) {
  const Icon = resolveIcon(item.icon);
  const ref = React.useRef<HTMLButtonElement>(null);
  return (
    <button
      ref={ref}
      type="button"
      title={item.tooltip ?? item.label}
      aria-pressed={item.toggle ? pressed : undefined}
      disabled={disabled}
      onClick={() => {
        if (ref.current && item.dropdown && !hasActionHandler(item.action)) {
          onOpenMenu(item, ref.current);
          return;
        }
        onActivate(item);
      }}
      className={cn(
        "flex h-[66px] w-16 shrink-0 flex-col items-center gap-1 px-0.5 pt-1.5",
        buttonSkin(disabled, pressed),
      )}
    >
      <Icon size={24} strokeWidth={1.6} className="shrink-0" />
      <span className="flex items-start justify-center text-center text-[11px] leading-[1.15]">
        <span className="line-clamp-2">{item.label}</span>
        {item.dropdown ? (
          <ChevronDown
            size={11}
            className="mt-[3px] ml-0.5 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled && ref.current) onOpenMenu(item, ref.current);
            }}
          />
        ) : null}
      </span>
    </button>
  );
}

function SmallItem({ item, disabled, pressed, onActivate, onOpenMenu }: ItemProps) {
  const Icon = resolveIcon(item.icon);
  const ref = React.useRef<HTMLButtonElement>(null);
  return (
    <button
      ref={ref}
      type="button"
      title={item.tooltip ?? item.label}
      aria-pressed={item.toggle ? pressed : undefined}
      disabled={disabled}
      onClick={() => {
        if (ref.current && item.dropdown && !hasActionHandler(item.action)) {
          onOpenMenu(item, ref.current);
          return;
        }
        onActivate(item);
      }}
      className={cn(
        "flex h-[21px] w-full items-center gap-1.5 px-1.5 text-left text-[11.5px] leading-none",
        buttonSkin(disabled, pressed),
      )}
    >
      <Icon size={14} strokeWidth={1.7} className="shrink-0" />
      <span className="truncate">{item.label}</span>
      {item.dropdown ? <ChevronDown size={10} className="ml-auto shrink-0" /> : null}
    </button>
  );
}

// ---------------------------------------------------------------- dropdown ---

interface MenuState {
  item: RibbonItem;
  x: number;
  y: number;
}

function DropdownMenu({ state, onClose }: { state: MenuState; onClose: () => void }) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Capture phase: a click on another ribbon button must close this menu
    // before that button's own handler opens its menu.
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      className="fixed z-50 min-w-44 rounded border border-ads-border-strong bg-ads-surface py-1 shadow-lg"
      style={{ left: state.x, top: state.y }}
    >
      {state.item.dropdown?.map((entry) => (
        <button
          key={entry.id}
          role="menuitem"
          className="block w-full px-3 py-1.5 text-left text-[12px] text-ads-text hover:bg-ads-hover"
          onClick={() => {
            dispatchAction(entry.action, { itemId: state.item.id, entryId: entry.id });
            onClose();
          }}
        >
          {entry.label}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ group ---

type Slot = { kind: "large"; item: RibbonItem } | { kind: "column"; items: RibbonItem[] };

/** Small items stack three per column, Office-style; large items stand alone. */
function toSlots(items: RibbonItem[]): Slot[] {
  const slots: Slot[] = [];
  let column: RibbonItem[] = [];
  const flush = () => {
    if (column.length) {
      slots.push({ kind: "column", items: column });
      column = [];
    }
  };
  for (const item of items) {
    if (item.size === "large") {
      flush();
      slots.push({ kind: "large", item });
    } else {
      column.push(item);
      if (column.length === 3) flush();
    }
  }
  flush();
  return slots;
}

function GroupView({
  group,
  render,
}: {
  group: RibbonGroup;
  render: (item: RibbonItem) => React.ReactNode;
}) {
  const slots = React.useMemo(() => toSlots(group.items), [group]);
  return (
    <div className="flex h-full shrink-0 flex-col justify-between border-r border-ads-border px-2 pt-1 pb-0.5 last:border-r-0">
      <div className="flex flex-1 items-start gap-0.5">
        {slots.map((slot, i) =>
          slot.kind === "large" ? (
            <React.Fragment key={slot.item.id}>{render(slot.item)}</React.Fragment>
          ) : (
            <div key={`col-${i}`} className="flex w-[132px] flex-col gap-[3px] pt-1">
              {slot.items.map((item) => (
                <React.Fragment key={item.id}>{render(item)}</React.Fragment>
              ))}
            </div>
          ),
        )}
      </div>
      <div className="pt-0.5 text-center text-[10.5px] leading-none text-ads-muted">{group.label}</div>
    </div>
  );
}

// ------------------------------------------------------------------ ribbon ---

function RibbonModeButtons() {
  const mode = useRibbonMode();
  const setMode = useAppStore((s) => s.setRibbonMode);
  const btn = (id: RibbonMode, label: string, Icon: typeof Pin) => (
    <button
      type="button"
      title={label}
      aria-pressed={mode === id}
      onClick={() => setMode(id)}
      className={cn(
        "flex h-5 w-5 items-center justify-center rounded",
        mode === id ? "bg-ads-accent-soft text-ads-accent" : "text-ads-muted hover:bg-ads-hover",
      )}
    >
      <Icon size={12} />
    </button>
  );
  return (
    <div className="flex items-center gap-0.5 px-1">
      {btn("pinned", "Pin Ribbon", Pin)}
      {btn("collapsed", "Collapse Ribbon", ChevronsUp)}
      {btn("autohide", "Auto-hide Ribbon", EyeOff)}
    </div>
  );
}

export function Ribbon({ visible }: { visible: boolean }) {
  const activeTab = useActiveTab();
  const mode = useRibbonMode();
  const setMode = useAppStore((s) => s.setRibbonMode);
  const setToggle = useAppStore((s) => s.setToggle);
  const toggles = useToggles();
  const ctx = useRibbonContext();
  const [menu, setMenu] = React.useState<MenuState | null>(null);

  // Some workspaces have no band of their own and borrow the one that fits:
  // the two modality pages want the run controls, not File's. Falling through
  // to RIBBON_TABS[0] put File's buttons above the DWI page, which is a
  // toolbar describing a different screen than the one you are looking at.
  const BORROWS: Record<string, string> = { dwi: "pipeline", perfusion: "pipeline" };
  const wanted = BORROWS[activeTab] ?? activeTab;
  const tab =
    RIBBON_TABS.find((t) => t.id === wanted) ??
    RIBBON_TABS.find((t) => t.id === "home") ??
    RIBBON_TABS[0];
  const frequentGroups = React.useMemo(
    () =>
      tab.groups
        .filter((group) => tab.id !== "home" || ["home.project", "home.data", "home.settings"].includes(group.id))
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => item.size === "large" || item.dropdown),
        }))
        .filter((group) => group.items.length > 0),
    [tab],
  );

  const openMenu = React.useCallback((item: RibbonItem, anchor: HTMLElement) => {
    const rect = anchor.getBoundingClientRect();
    setMenu({ item, x: rect.left, y: rect.bottom + 2 });
  }, []);

  const activate = React.useCallback(
    (item: RibbonItem) => {
      if (item.toggle) {
        const next = !(useAppStore.getState().toggles[item.id] ?? false);
        setToggle(item.id, next);
        dispatchAction(item.action, { itemId: item.id, pressed: next });
        return;
      }
      dispatchAction(item.action, { itemId: item.id });
    },
    [setToggle],
  );

  const renderItem = React.useCallback(
    (item: RibbonItem) => {
      if (item.hidden?.(ctx)) return null;
      const props: ItemProps = {
        item,
        disabled: item.disabled?.(ctx) ?? false,
        pressed: item.toggle ? (toggles[item.id] ?? false) : false,
        onActivate: activate,
        onOpenMenu: openMenu,
      };
      return item.size === "large" ? <LargeItem {...props} /> : <SmallItem {...props} />;
    },
    [ctx, toggles, activate, openMenu],
  );

  if (!visible) {
    // The way back has to be the whole strip, not three unlabelled 20px icons
    // at the far right of it. Those are technically sufficient and practically
    // invisible: hide the toolbar once and the honest conclusion is that it
    // cannot be brought back.
    const hidden = mode === "autohide";
    return (
      <div className="flex h-6 shrink-0 items-stretch border-b border-ads-border bg-ads-surface">
        <button
          type="button"
          onClick={() => setMode("pinned")}
          title="Show the toolbar and keep it open"
          className="group flex min-w-0 flex-1 items-center gap-1.5 px-2 text-left text-[10.5px] text-ads-muted hover:bg-ads-hover hover:text-ads-text"
        >
          <ChevronDown size={12} className="shrink-0" />
          <span className="truncate">
            {hidden ? "Toolbar hidden" : "Toolbar collapsed"} — click to show it
          </span>
        </button>
        <div className="flex items-center border-l border-ads-border">
          <RibbonModeButtons />
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 border-b border-ads-border bg-ads-surface">
      <div
        role="toolbar"
        aria-label={`${tab.label} ribbon`}
        className="flex items-stretch overflow-x-auto overflow-y-hidden px-1"
        style={{ height: RIBBON_BAND_HEIGHT }}
      >
        {frequentGroups.map((group) => (
          <GroupView key={group.id} group={group} render={renderItem} />
        ))}
        <div className="ml-auto flex items-end pb-1">
          <RibbonModeButtons />
        </div>
      </div>
      {menu ? <DropdownMenu state={menu} onClose={() => setMenu(null)} /> : null}
    </div>
  );
}
